import React, { PureComponent } from 'react';
import { I } from '../../../../common/v5/config';
import { FormInputWithLabelRow, TextInputRow, CheckboxInputRow } from '../../../../reactcomponents/Form';
import { NoFlipMultiSelect } from '../../../../reactcomponents/Dropdown';
import { TYPE_OPTIONS } from '../../../../common/v5/constants';
import HelpTooltip from './HelpTooltip';
import './ToolForm.css';

class ToolForm extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize state with tool data if editing
    const { tool } = props;
    
    this.state = {
      showArea: false,
      selectedTemplate: "custom",
      parameters: this.initializeParameters(props),
      labelHelper: I("You can add your own parameters manually if you don't want to use a predefined template."),
      parameterToAdd: "__select__",
      formData: {
        toolName: tool?.toolName || tool?.name || '',
        toolDescription: tool?.toolDescription || tool?.description || '',
        selectedArea: this.initializeSelectedArea(tool),
        resp_text: tool?.resp_text || ''
      }
    };

    // Bind methods
    this.handleToggleArea = this.handleToggleArea.bind(this);
    this.handleSelectArea = this.handleSelectArea.bind(this);
    this.handleAddParameterType = this.handleAddParameterType.bind(this);
    this.handleRemoveParameter = this.handleRemoveParameter.bind(this);
    this.handleParameterChange = this.handleParameterChange.bind(this);
    this.handleInternalChange = this.handleInternalChange.bind(this);
    this.handleTemplateChange = this.handleTemplateChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSelectParameterToAdd = this.handleSelectParameterToAdd.bind(this);
  }

  initializeSelectedArea(tool) {
    if (!tool) return "";
    
    // Handle different area formats from the tool data
    // First check originalData which contains the backend data structure
    if (tool.originalData && tool.originalData.areas && Array.isArray(tool.originalData.areas)) {
      return tool.originalData.areas.join(",");
    }
    
    // Then check direct properties
    if (tool.areas && Array.isArray(tool.areas)) {
      return tool.areas.join(",");
    }
    if (tool.selectedArea) {
      return tool.selectedArea;
    }
    if (tool.area) {
      return typeof tool.area === 'string' ? tool.area : tool.area.toString();
    }
    
    // Check originalData for other area fields
    if (tool.originalData) {
      if (tool.originalData.selectedArea) {
        return tool.originalData.selectedArea;
      }
      if (tool.originalData.area) {
        return typeof tool.originalData.area === 'string' ? tool.originalData.area : tool.originalData.area.toString();
      }
    }
    
    return "";
  }

  initializeParameters(props) {
    const { tool } = props;
    if (!tool) return [];
    
    let parameters = [];
    
    // Handle different parameter formats
    if (tool.parameters) {
      if (Array.isArray(tool.parameters)) {
        parameters = tool.parameters;
      } else if (typeof tool.parameters === 'string') {
        try {
          parameters = JSON.parse(tool.parameters);
          if (!Array.isArray(parameters)) {
            parameters = [];
          }
        } catch (e) {
          console.warn('Failed to parse tool parameters:', e);
          parameters = [];
        }
      }
    }
    
    // Transform parameters to match form structure
    return parameters.map(param => ({
      name: param.name || '',
      type: param.type || 'String',
      description: param.description || '',
      enum: param.enumValue || param.enum || '',
      internal: param.internal || false
    }));
  }

  componentDidUpdate(prevProps) {
    // Update form data when tool prop changes
    if (prevProps.tool !== this.props.tool) {
      const { tool } = this.props;
      const newFormData = {
        toolName: tool?.toolName || tool?.name || '',
        toolDescription: tool?.toolDescription || tool?.description || '',
        selectedArea: this.initializeSelectedArea(tool),
        resp_text: tool?.resp_text || ''
      };
      this.setState({
        parameters: this.initializeParameters(this.props),
        formData: newFormData
      });
    }
  }

  // Notify parent of changes
  notifyChange() {
    const { onChange } = this.props;
    if (onChange) {
      const { formData, parameters } = this.state;
      onChange({
        ...formData,
        parameters: parameters
      });
    }
  }

  templates = {
		check_order_status: {
			toolName: "check_order_status",
			toolDescription: "Retrieves the current status of a specific order, indicating whether it has been processed, is pending, or other relevant states.",
			parameters: [
				{ name: "order_id", type: "String", description: "The unique identifier of the order whose status is to be checked.", enum: "", internal: false },
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically GET for retrieving data.", enum: "GET", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint to query for order status.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to check the status of an existing order. Provide an order ID and authorization to retrieve the current order status.")
		},
		delete_order: {
			toolName: "delete_order",
			toolDescription: "Initiates the cancellation and removal of an existing order from the system.",
			parameters: [
				{ name: "order_id", type: "String", description: "The unique identifier of the order to be cancelled and removed.", enum: "", internal: false },
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically POST or DELETE for destructive actions.", enum: "POST", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint used for deleting an order.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to delete an order. Provide the order ID and authorization to proceed with the cancellation.")
		},
		check_rewards_points: {
			toolName: "check_rewards_points",
			toolDescription: "Fetches the current rewards points balance for a specified customer.",
			parameters: [
				{ name: "customer_id", type: "String", description: "The unique identifier of the customer whose rewards points balance is to be checked.", enum: "", internal: false },
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically GET for retrieving data.", enum: "GET", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint to query for customer rewards points.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to check a customer's rewards points balance. Requires a valid customer ID and authorization.")
		},
		delivery_status: {
			toolName: "delivery_status",
			toolDescription: "Retrieves the delivery status of an order, including shipping information and current location if available.",
			parameters: [
				{ name: "order_id", type: "Integer", description: "The unique identifier of the order whose delivery status is to be tracked.", enum: "", internal: false },
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically GET for retrieving data.", enum: "GET", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint used for tracking order delivery status.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to track an order's delivery status. Provide the order ID and authorization for tracking.")
		},
		get_products_list: {
			toolName: "get_products_list",
			toolDescription: "Fetches a comprehensive list of all available products and/or services.",
			parameters: [
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically GET for retrieving data.", enum: "GET", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint that provides the list of products/services.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to retrieve a list of available products and services. Requires authorization.")
		},
		create_order: {
			toolName: "create_order",
			toolDescription: "Creates a new order for a specified product with a given quantity.",
			parameters: [
				{ name: "sku", type: "String", description: "The Stock Keeping Unit (SKU) or unique identifier of the product to be ordered.", enum: "", internal: false },
				{ name: "quantity", type: "Integer", description: "The number of units of the specified product to include in the order.", enum: "", internal: false },
				{ name: "http_request", type: "String", description: "The HTTP method to be used for the API call. Typically POST for creating new resources.", enum: "POST", internal: true },
				{ name: "url", type: "String", description: "The complete URL of the API endpoint used for creating new orders.", enum: "", internal: true },
				{ name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
			],
			labelHelper: I("Use this function to create a new order. Provide the product SKU and desired quantity. Requires authorization.")
		},
		order_status_inquiry: {
			toolName: "order_status_inquiry",
			toolDescription: "Inquires or checks the status of a customer's order, providing details on its current state, such as processing, shipping, or delivery updates.",
			parameters: [
				{ name: "order_number", type: "string", description: "A unique order identifier or number used for tracking and status inquiries.", enum: "", internal: false },
				{ name: "template_text", type: "string", description: "Defines the template for the response message. This template can include placeholders (e.g., {order_number}) and is used to generate the final text, which typically contains a link to the order status page.", enum: "", internal: true }
			],
			labelHelper: I("Use this function to generate a predefined text that can include order data like {order_number} with link to the order status page.")
		},
		search_product: {
			toolName: "search_product",
			toolDescription: "Retrieves detailed product information by searching a product catalog using a natural language description. This tool is useful whenever product details are needed to answer a user's question, or when the user is looking for specific items described by their features, characteristics, or type, rather than by a precise name or ID. For example, use it if the user asks 'what are the specifications of your HSS drill bit?' or 'tell me more about your red wollen sweaters' or 'find an anorak jacket for hiking'.",
			parameters: [
				{ name: "product_description", type: "string", description: "A natural language phrase describing the key features, characteristics, or type of product the user is looking for. For instance, 'durable waterproof hiking boots size 10' or 'organic green tea bags'.", enum: "", internal: false },
				{ name: "url", type: "string", description: "The URL of the feed to lookup the product details", enum: "", internal: true }
			],
			labelHelper: I("Use this function to search products by natural language from a JSON feed URL.")
		},
		web_search: {
			toolName: "web_search",
			toolDescription: "Performs a web search specifically for information about [company name], including its products, services, contacts, news, or other company-related details. Use this tool to gather missing context needed to fully address a user's query or for factual lookups about [company name] when existing information is insufficient or other specialized tools are not applicable.",
			parameters: [
				{ name: "query", type: "string", description: "The specific search query string. For example, '[company name] product features' or 'contact [company name] support'.", enum: "", internal: false },
				{ name: "include_domains", type: "string", description: "A list of domain names to restrict the search to.", enum: "", internal: true },
				{ name: "exclude_domains", type: "string", description: "A list of domain names to exclude from the search.", enum: "", internal: true },
				{ name: "language", type: "string", description: "The desired ISO 639-1 language code of the web content.", enum: "en", internal: true },
				{ name: "api_key", type: "string", description: "The API key for the web search service.", enum: "", internal: true }
			],
			labelHelper: I("Use this function to perform a web search for product and service related information from a web url.")
		}
	};

  predefinedParameters = {
    "http_request": { name: "http_request", type: "String", description: "The HTTP method for the request (e.g., GET, POST, DELETE).", enum: "", internal: true },
    "url": { name: "url", type: "String", description: "The target URL for the service or API endpoint.", enum: "", internal: true },
    "header_authorization": { name: "header.Authorization", type: "String", description: "The authorization token (e.g., Bearer token) required to authenticate with the API.", enum: "", internal: true },
    "template_text": { name: "template_text", type: "String", description: "A template or format for the response message text.", enum: "", internal: true },
    "include_domains": { name: "include_domains", type: "string", description: "A list of specific domain names to include in the operation.", enum: "", internal: true },
    "exclude_domains": { name: "exclude_domains", type: "string", description: "A list of specific domain names to exclude from the operation.", enum: "", internal: true },
    "language": { name: "language", type: "string", description: "The ISO 639-1 language code for content processing or output.", enum: "en", internal: true },
    "api_key": { name: "api_key", type: "string", description: "The API key required for accessing an external service or API.", enum: "", internal: true }
  };

  handleToggleArea() {
    this.setState(prevState => ({ showArea: !prevState.showArea }));
  }

  handleSelectArea(selectedItems) {
    // Handle both string and array inputs
    let selectedAreaStr = '';
    
    if (typeof selectedItems === 'string') {
      // If it's already a string (comma-separated IDs), use it directly
      selectedAreaStr = selectedItems;
    } else if (Array.isArray(selectedItems)) {
      // If it's an array, map to IDs and join with comma
      selectedAreaStr = selectedItems.map(item => item.id || item.Id).join(",");
    } else if (selectedItems && (selectedItems.id || selectedItems.Id)) {
      // If it's a single object, get its ID
      selectedAreaStr = (selectedItems.id || selectedItems.Id).toString();
    }

    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        selectedArea: selectedAreaStr
      }
    }), () => {
      this.notifyChange();
    });
  }

  handleAddParameterType() {
    const { parameterToAdd } = this.state;
    if (parameterToAdd && parameterToAdd !== "__select__") {
      let newParam;
      
      if (parameterToAdd === "custom") {
        // For custom parameters, create empty parameter for user to fill
        newParam = {
          name: "",
          type: "String",
          description: "",
          enum: "",
          internal: false
        };
      } else {
        // For predefined parameters, use the predefined data
        newParam = this.predefinedParameters[parameterToAdd] || {
          name: parameterToAdd,
          type: "String",
          description: "",
          enum: "",
          internal: false
        };
      }

      this.setState(prevState => ({
        parameters: [...prevState.parameters, newParam],
        parameterToAdd: "__select__"
      }), () => {
        this.notifyChange();
      });
    }
  }

  handleRemoveParameter(index) {
    this.setState(prevState => ({
      parameters: prevState.parameters.filter((_, i) => i !== index)
    }), () => {
      this.notifyChange();
    });
  }

  handleParameterChange(index, field, value) {
    this.setState(prevState => ({
      parameters: prevState.parameters.map((param, i) => 
        i === index ? { ...param, [field]: value } : param
      )
    }), () => {
      this.notifyChange();
    });
  }

  // Validation methods
  validateParameters() {
    const { parameters } = this.state;
    const errors = [];

    // Check for duplicate parameter names
    const parameterNames = parameters.map(param => param.name.toLowerCase().trim()).filter(name => name);
    const duplicateNames = parameterNames.filter((name, index) => parameterNames.indexOf(name) !== index);
    
    if (duplicateNames.length > 0) {
      errors.push(`Duplicate parameter names found: ${[...new Set(duplicateNames)].join(', ')}`);
    }

    // TEMPORARILY COMMENTED OUT - Check that internal parameters have enum values
    // TODO: Find better way to handle this validation
    // parameters.forEach((param, index) => {
    //   if (param.internal && (!param.enum || param.enum.trim() === '')) {
    //     errors.push(`Parameter "${param.name}" (index ${index + 1}) is marked as internal but has no enum value configured.`);
    //   }
    // });

    return errors;
  }

  getValidationErrors() {
    return this.validateParameters();
  }

  handleInternalChange(index, checked) {
    this.handleParameterChange(index, 'internal', checked);
  }

  handleTemplateChange(e) {
    const selectedTemplate = e.target.value;
    let newParameters = [];
    let newDescription = "";
    let newLabelHelper = I("You can add your own parameters manually if you don't want to use a predefined template.");

    if (selectedTemplate !== "custom" && this.templates[selectedTemplate]) {
      const template = this.templates[selectedTemplate];
      newParameters = [...template.parameters];
      newDescription = template.description;
      newLabelHelper = template.description;
    }

    this.setState({
      selectedTemplate,
      parameters: newParameters,
      labelHelper: newLabelHelper,
      formData: {
        ...this.state.formData,
        toolDescription: selectedTemplate !== "custom" ? newDescription : this.state.formData.toolDescription
      }
    }, () => {
      this.notifyChange();
    });
  }

  handleInputChange(field, value) {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }), () => {
      this.notifyChange();
    });
  }

  handleSelectParameterToAdd(e) {
    const value = e.target.value;
    this.setState({ parameterToAdd: value }, () => {
      if (value !== "__select__") {
        this.handleAddParameterType();
      }
    });
  }

  render() {
    const { areaList = [], tool } = this.props;
    const { formData, parameters } = this.state;
    const validationErrors = this.getValidationErrors();
    // Only disable function name when editing an existing tool (non-zero id). Create New Tool has id: 0.
    const isEditing = !!(
      tool &&
      ((tool.id != null && tool.id !== 0 && tool.id !== '0') ||
        (tool.tool_id != null && tool.tool_id !== 0 && tool.tool_id !== '0'))
    );

    return (
      <div className="admin-one-form custom">
        <div className="tool-manager">
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="validation-errors" style={{ 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              padding: '10px', 
              marginBottom: '15px' 
            }}>
              <h4 style={{ color: '#c33', margin: '0 0 8px 0', fontSize: '14px' }}>
                {I('Validation Errors:')}
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index} style={{ color: '#c33', fontSize: '13px', marginBottom: '4px' }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Template Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label">
              {I("Function Template")}
              <HelpTooltip 
                content={`<strong>${I("Selected Template:")}</strong> ${this.state.selectedTemplate}<br/><strong>${I("Description:")}</strong> ${this.state.labelHelper}`}
                position="right"
              />
            </div>
            <div className="form-input-container">
              <select
                id="templateSelector"
                value={this.state.selectedTemplate}
                onChange={this.handleTemplateChange}
                className="form-control styled-select"
              >
                <option value="custom">{I("Custom")}</option>
                {Object.keys(this.templates).map(template => (
                  <option key={template} value={template}>{template}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Area Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Area")}
            </div>
            <div className="form-input-container">
              <NoFlipMultiSelect
                id={"area"}
                title={I("Select Area")}
                className={"popup-multi-select"}
                overrideTitle={true}
                groupSelect={true}
                nested={{ key: 'Areas' }}
                data={areaList}
                idFields={{id: "Id", name: "Name"}}
                selected={formData.selectedArea}
                onSelect={this.handleSelectArea}
                onToggle={this.handleToggleArea}
                show={this.state.showArea}
                selectAll={true}
                selectNone={true}
              />
            </div>
          </div>

          {/* Function Name */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Function Name")}
            </div>
            <div className="form-input-container">
              <input
                type="text"
                id="toolName"
                name="toolName"
                value={formData.toolName}
                onChange={(e) => this.handleInputChange('toolName', e.target.value)}
                className="admin-text-input"
                placeholder={I("Enter function name")}
                disabled={isEditing}
                style={isEditing ? { backgroundColor: '#f5f5f5', color: '#999', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          {/* Function Description */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Function Description")}
            </div>
            <div className="form-input-container">
              <textarea
                id="toolDescription"
                name="toolDescription"
                value={formData.toolDescription}
                onChange={(e) => this.handleInputChange('toolDescription', e.target.value)}
                className="admin-text-input"
                rows={4}
                placeholder={I("Enter function description")}
              />
            </div>
          </div>

          {/* Parameters Section */}
          <div className="tool-parameters-section">
            <h4>{I("Parameters")}</h4>
            <div className="parameters-container">
              {parameters.length === 0 && (
                <p>{I("No parameters defined. Add parameters using the dropdown below.")}</p>
              )}
              {parameters.map((param, index) => (
                <div key={index} className="parameter-section">
                  <div className="title-prompt">Parameter {index + 1}</div>
                  
                  {/* Parameter Name */}
                  <div className="form-input-with-label-row">
                    <div className="form-label">
                      {I("Name")}
                    </div>
                    <div className="form-input-container">
                      <input
                        type="text"
                        id={`param-name-${index}`}
                        value={param.name}
                        onChange={(e) => this.handleParameterChange(index, 'name', e.target.value)}
                        className="admin-text-input"
                        placeholder={I("Parameter name")}
                      />
                    </div>
                  </div>

                  {/* Parameter Type */}
                  <div className="form-input-with-label-row">
                    <div className="form-label">
                      {I("Type")}
                    </div>
                    <div className="form-input-container">
                      <select
                        value={param.type}
                        onChange={(e) => this.handleParameterChange(index, 'type', e.target.value)}
                        className="form-control styled-select"
                      >
                        <option value="" disabled>{I("Select Type")}</option>
                        {TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Parameter Description */}
                  <div className="form-input-with-label-row">
                    <div className="form-label">
                      {I("Description")}
                    </div>
                    <div className="form-input-container">
                      <textarea
                        id={`param-desc-${index}`}
                        value={param.description}
                        onChange={(e) => this.handleParameterChange(index, 'description', e.target.value)}
                        className="admin-text-input"
                        rows={3}
                        placeholder={I("Parameter description")}
                      />
                    </div>
                  </div>

                  {/* Parameter Enum */}
                  <div className="form-input-with-label-row">
                    <div className="form-label">
                      {I("Enum (if any)")}
                    </div>
                    <div className="form-input-container">
                      <textarea
                        id={`param-enum-${index}`}
                        value={param.enum}
                        onChange={(e) => this.handleParameterChange(index, 'enum', e.target.value)}
                        className="admin-text-input"
                        rows={2}
                        placeholder={I("Comma-separated values (optional)")}
                      />
                    </div>
                  </div>

                  {/* Internal Checkbox */}
                  <div className="checkbox-input-row">
                    <input
                      type="checkbox"
                      id={`param-internal-${index}`}
                      checked={param.internal}
                      onChange={(e) => this.handleInternalChange(index, e.target.checked)}
                    />
                    <label htmlFor={`param-internal-${index}`}>
                      {I("Internal Parameter")}
                    </label>
                  </div>

                  {/* Remove Parameter Button */}
                  <div className="tool-remove-parameter-container">
                    <button
                      type="button"
                      className="removeParameterButton"
                      onClick={() => this.handleRemoveParameter(index)}
                    >
                      {I("Remove Parameter")}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Parameter */}
            <div className="tool-add-parameter-wrapper">
              <label htmlFor="add-parameter-select">{I("Add Parameter:")}</label>
              <select
                id="add-parameter-select"
                value={this.state.parameterToAdd}
                onChange={this.handleSelectParameterToAdd}
                className="tool-add-parameter-select form-control styled-select"
              >
                <option value="__select__" disabled>{I("Select parameter to add...")}</option>
                <option value="custom">{I("Custom Parameter")}</option>
                <optgroup label={I("Predefined Parameters")}>
                  {Object.keys(this.predefinedParameters).map(key => (
                    <option key={key} value={key}>{this.predefinedParameters[key].name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ToolForm; 